<script setup>
definePage({
  name: 'shipment-warehouse-pl-files',
  meta: {
    requiresAuth: true,
    action: 'read',
    subject: 'Parcels',
  },
})

// "Fichiers PL": upload the original ARRIVAGE packing list of a container and
// download FINAL 7 / PRODUCT LIST / GODANA / GOING, generated on the server
// (PlFilesBuilder). Product names missing from the glossary can be translated
// here; saving them regenerates the files.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'

const { t } = useI18n()
const { showSnackbar } = useSnackbar()
const route = useRoute()

const containerId = computed(() => route.params.containerId)

const container = ref(null)
const sets = ref([])
const isLoading = ref(false)
const isUploading = ref(false)
const uploadProgress = ref(0)
const fileInput = ref(null)
const selectedFile = ref(null)
const downloading = ref(null)

// translations being typed: cn -> { en, fr }
const terms = ref({})
const isSavingTerms = ref(false)

const KIND_LABELS = {
  final: 'FINAL 7',
  plist: 'PRODUCT LIST',
  godana: 'GODANA',
  going: 'GOING',
}

const STATUS = {
  pending: { label: 'En attente', color: 'secondary' },
  processing: { label: 'Génération en cours…', color: 'info' },
  done: { label: 'Terminé', color: 'success' },
  failed: { label: 'Échec', color: 'error' },
}

const latest = computed(() => sets.value[0] || null)
const history = computed(() => sets.value.slice(1))
const isBusy = computed(() => ['pending', 'processing'].includes(latest.value?.status))

const fmtSize = b => (b ? `${(b / 1048576).toFixed(1)} Mo` : '')
const fmtDate = s => (s ? new Date(s).toLocaleString('fr-FR') : '')

let pollTimer = null

const load = async () => {
  isLoading.value = true
  try {
    const { data } = await axios.get(`shipment/warehouse/containers/${containerId.value}/pl-files`)

    container.value = data.data.container
    sets.value = data.data.sets

    // Keep what is already typed; otherwise pre-fill with the machine
    // suggestion (to be checked before saving).
    const next = {}
    for (const m of latest.value?.missing_terms || []) {
      const cur = terms.value[m.cn] || { en: '', fr: '' }

      next[m.cn] = {
        en: cur.en || m.suggestion?.en || '',
        fr: cur.fr || m.suggestion?.fr || '',
      }
    }
    terms.value = next
  } catch (e) {
    console.error('pl-files load failed:', e)
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  } finally {
    isLoading.value = false
  }
  schedulePoll()
}

const isSuggesting = computed(() => latest.value?.suggestions_status === 'running')

const schedulePoll = () => {
  clearTimeout(pollTimer)
  if (isBusy.value || isSuggesting.value) pollTimer = setTimeout(load, 3000)
}

const requestSuggestions = async () => {
  if (!latest.value) return
  try {
    await axios.post(`shipment/warehouse/pl-files/${latest.value.id}/suggest`)
    await load()
  } catch (e) {
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  }
}

const pickFile = () => fileInput.value?.click()

const onFile = e => {
  selectedFile.value = e.target.files?.[0] || null
  e.target.value = ''
}

const upload = async () => {
  if (!selectedFile.value) return
  isUploading.value = true
  uploadProgress.value = 0
  try {
    const form = new FormData()

    form.append('excel_file', selectedFile.value)
    await axios.post(`shipment/warehouse/containers/${containerId.value}/pl-files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: ev => {
        if (ev.total) uploadProgress.value = Math.round((ev.loaded / ev.total) * 100)
      },
    })
    selectedFile.value = null
    showSnackbar({ color: 'success', text: 'PL envoyée — génération des fichiers en cours.' })
    await load()
  } catch (e) {
    console.error('pl-files upload failed:', e)
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  } finally {
    isUploading.value = false
  }
}

// Ask for a short-lived signed link, then let the browser download the file
// natively (progress bar, no waiting for the whole file inside the page).
const download = async (set, kind) => {
  downloading.value = `${set.id}-${kind}`
  try {
    const { data } = await axios.get(`shipment/warehouse/pl-files/${set.id}/download-link/${kind}`)

    window.location.assign(data.data.url)
  } catch (e) {
    console.error('pl-files download failed:', e)
    showSnackbar({ color: 'error', text: t('messages.something_went_wrong') })
  } finally {
    // the browser takes over the download; release the button quickly
    setTimeout(() => { downloading.value = null }, 1500)
  }
}

const regenerate = async set => {
  try {
    await axios.post(`shipment/warehouse/pl-files/${set.id}/regenerate`)
    await load()
  } catch (e) {
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  }
}

const filledTerms = computed(() => Object.entries(terms.value)
  .filter(([, v]) => v.en.trim() && v.fr.trim())
  .map(([cn, v]) => ({ cn, en: v.en.trim(), fr: v.fr.trim() })))

const saveTerms = async () => {
  if (!filledTerms.value.length) return
  isSavingTerms.value = true
  try {
    await axios.post('shipment/warehouse/pl-glossary', {
      terms: filledTerms.value,
      regenerate_set_id: latest.value?.id,
    })
    showSnackbar({ color: 'success', text: `${filledTerms.value.length} traduction(s) enregistrée(s) — régénération en cours.` })
    await load()
  } catch (e) {
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  } finally {
    isSavingTerms.value = false
  }
}

onMounted(load)
onBeforeUnmount(() => clearTimeout(pollTimer))
</script>

<template>
  <div>
    <VCard class="mb-4">
      <VCardText class="d-flex flex-wrap align-center gap-2">
        <h2 class="text-h4 me-4">
          Fichiers PL
        </h2>
        <VChip
          v-if="container"
          color="primary"
          variant="elevated"
          prepend-icon="tabler-container"
        >
          {{ container.reference_code }}
        </VChip>
        <VSpacer />
        <VBtn
          variant="tonal"
          size="small"
          prepend-icon="tabler-calculator"
          :to="`/shipment/warehouse/cotation/${containerId}`"
        >
          Cotation
        </VBtn>
      </VCardText>

      <!-- Upload -->
      <VCardText class="pt-0">
        <p class="text-body-2 mb-3">
          Envoyer la <strong>PL ARRIVAGE originale</strong> (ex. <code>9.30.ARRIVAGE_NOVEMBRE_2026_7eVague.xlsx</code>, avec les photos) —
          pas le fichier « BY SEA ». Les fichiers <strong>FINAL 7</strong>, <strong>PRODUCT LIST</strong>, <strong>GODANA</strong>
          et <strong>GOING</strong> sont générés automatiquement (environ 1 minute).
        </p>
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx"
          hidden
          @change="onFile"
        >
        <div class="d-flex flex-wrap align-center gap-3">
          <VBtn
            variant="outlined"
            prepend-icon="tabler-file-upload"
            :disabled="isUploading"
            @click="pickFile"
          >
            Choisir la PL…
          </VBtn>
          <span
            v-if="selectedFile"
            class="text-body-2"
          >{{ selectedFile.name }} ({{ fmtSize(selectedFile.size) }})</span>
          <VBtn
            color="primary"
            prepend-icon="tabler-wand"
            :disabled="!selectedFile || isUploading || isBusy"
            :loading="isUploading"
            @click="upload"
          >
            Générer les fichiers
          </VBtn>
        </div>
        <VProgressLinear
          v-if="isUploading"
          :model-value="uploadProgress"
          color="primary"
          class="mt-3"
          height="6"
        />
      </VCardText>
    </VCard>

    <VProgressLinear
      v-if="isLoading && !sets.length"
      indeterminate
      color="primary"
    />

    <!-- Latest generation -->
    <VCard
      v-if="latest"
      class="mb-4"
    >
      <VCardText>
        <div class="d-flex flex-wrap align-center gap-2 mb-3">
          <span class="text-h6">{{ latest.original_filename }}</span>
          <VChip
            size="small"
            :color="STATUS[latest.status]?.color"
            variant="tonal"
          >
            <VProgressCircular
              v-if="isBusy"
              indeterminate
              size="12"
              width="2"
              class="me-2"
            />
            {{ STATUS[latest.status]?.label || latest.status }}
          </VChip>
          <span class="text-caption text-medium-emphasis">{{ fmtDate(latest.generated_at || latest.created_at) }}</span>
          <VSpacer />
          <VBtn
            v-if="!isBusy"
            size="small"
            variant="text"
            prepend-icon="tabler-refresh"
            @click="regenerate(latest)"
          >
            Régénérer
          </VBtn>
        </div>

        <VAlert
          v-if="latest.status === 'failed'"
          type="error"
          variant="tonal"
          class="mb-3"
        >
          {{ latest.error }}
        </VAlert>

        <div
          v-if="latest.stats"
          class="d-flex flex-wrap gap-6 mb-4"
        >
          <div>
            <div class="text-caption text-medium-emphasis">
              Lignes PL
            </div>
            <div class="text-body-1 font-weight-medium">
              {{ latest.stats.lines }}
            </div>
          </div>
          <div>
            <div class="text-caption text-medium-emphasis">
              Produits (FILTERED)
            </div>
            <div class="text-body-1 font-weight-medium">
              {{ latest.stats.items }}
            </div>
          </div>
          <div>
            <div class="text-caption text-medium-emphasis">
              Lignes GD / GB
            </div>
            <div class="text-body-1 font-weight-medium">
              {{ latest.stats.gd_lines }} / {{ latest.stats.gb_lines }}
            </div>
          </div>
        </div>

        <div class="d-flex flex-wrap gap-2">
          <VBtn
            v-for="f in latest.files"
            :key="f.kind"
            color="success"
            variant="tonal"
            prepend-icon="tabler-download"
            :loading="downloading === `${latest.id}-${f.kind}`"
            @click="download(latest, f.kind)"
          >
            {{ KIND_LABELS[f.kind] || f.kind }}
            <span class="text-caption ms-2">{{ fmtSize(f.size) }}</span>
          </VBtn>
          <VBtn
            variant="text"
            prepend-icon="tabler-file"
            :loading="downloading === `${latest.id}-original`"
            @click="download(latest, 'original')"
          >
            PL originale
          </VBtn>
        </div>

        <VExpansionPanels
          v-if="latest.anomalies?.length"
          class="mt-4"
          variant="accordion"
        >
          <VExpansionPanel :title="`${latest.anomalies.length} ligne(s) à vérifier (quantité lue automatiquement)`">
            <VExpansionPanelText>
              <div
                v-for="(a, i) in latest.anomalies"
                :key="i"
                class="text-body-2"
              >
                {{ a }}
              </div>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCardText>
    </VCard>

    <!-- Missing translations -->
    <VCard
      v-if="latest && latest.missing_terms?.length"
      class="mb-4"
    >
      <VCardText>
        <div class="d-flex flex-wrap align-center gap-2 mb-2">
          <span class="text-h6">{{ latest.missing_terms.length }} produit(s) à traduire</span>
          <VChip
            v-if="isSuggesting"
            size="small"
            color="info"
            variant="tonal"
          >
            <VProgressCircular
              indeterminate
              size="12"
              width="2"
              class="me-2"
            />
            Recherche de suggestions…
          </VChip>
          <VSpacer />
          <VBtn
            variant="tonal"
            prepend-icon="tabler-language"
            :disabled="isSuggesting || isBusy"
            @click="requestSuggestions"
          >
            Suggérer les traductions
          </VBtn>
          <VBtn
            color="primary"
            prepend-icon="tabler-device-floppy"
            :disabled="!filledTerms.length || isBusy"
            :loading="isSavingTerms"
            @click="saveTerms"
          >
            Enregistrer {{ filledTerms.length || '' }} et régénérer
          </VBtn>
        </div>
        <p class="text-body-2 text-medium-emphasis mb-3">
          Ces noms chinois ne sont pas encore dans le glossaire : leurs colonnes ENGLISH / FRENCH sont vides dans les fichiers.
          Les cases sont pré-remplies par une <strong>traduction automatique à vérifier</strong> (nom court et générique) avant
          d’enregistrer. Les traductions enregistrées servent pour toutes les prochaines PL.
        </p>
        <VTable density="compact">
          <thead>
            <tr>
              <th>Chinois</th>
              <th>Écrit dans la PL</th>
              <th>English</th>
              <th>Français</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="m in latest.missing_terms"
              :key="m.cn"
            >
              <td class="font-weight-medium">
                {{ m.cn }}
                <VChip
                  v-if="m.suggestion"
                  size="x-small"
                  variant="tonal"
                  color="info"
                  class="ms-1"
                >
                  suggestion · {{ m.suggestion.engine }}
                </VChip>
              </td>
              <td class="text-medium-emphasis">
                {{ m.seen_as }}
              </td>
              <td style="min-inline-size: 200px;">
                <VTextField
                  v-if="terms[m.cn]"
                  v-model="terms[m.cn].en"
                  density="compact"
                  hide-details
                  placeholder="e.g. storage box"
                />
              </td>
              <td style="min-inline-size: 200px;">
                <VTextField
                  v-if="terms[m.cn]"
                  v-model="terms[m.cn].fr"
                  density="compact"
                  hide-details
                  placeholder="ex. boîte de rangement"
                />
              </td>
            </tr>
          </tbody>
        </VTable>
      </VCardText>
    </VCard>

    <!-- Previous generations -->
    <VCard v-if="history.length">
      <VCardTitle class="text-h6">
        Générations précédentes
      </VCardTitle>
      <VList density="compact">
        <VListItem
          v-for="s in history"
          :key="s.id"
        >
          <VListItemTitle>{{ s.original_filename }}</VListItemTitle>
          <VListItemSubtitle>{{ fmtDate(s.generated_at || s.created_at) }} · {{ STATUS[s.status]?.label || s.status }}</VListItemSubtitle>
          <template #append>
            <div class="d-flex gap-1">
              <VBtn
                v-for="f in s.files"
                :key="f.kind"
                size="x-small"
                variant="text"
                :loading="downloading === `${s.id}-${f.kind}`"
                @click="download(s, f.kind)"
              >
                {{ KIND_LABELS[f.kind] }}
              </VBtn>
            </div>
          </template>
        </VListItem>
      </VList>
    </VCard>
  </div>
</template>
